import React, { useEffect, useState } from 'react';
import { TrophyIcon, ChartBarIcon, StarIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import BadgeDisplay from './BadgeDisplay';

interface Reputation {
  userId: string;
  totalPoints: number;
  level: number;
  nextLevelPoints: number;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
}

const ReputationDashboard: React.FC = () => {
  const [reputation, setReputation] = useState<Reputation | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReputation();
    fetchBadges();
    fetchAllBadges();
  }, []);

  const fetchReputation = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/reputation/me/score');
      setReputation(response.data);
    } catch (err: any) {
      console.error('Error fetching reputation:', err);
      setError(err.response?.data?.error || 'Failed to load reputation');
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const response = await axios.get('/api/reputation/me/badges');
      setBadges(response.data);
    } catch (err) {
      console.error('Error fetching badges:', err);
    }
  };

  const fetchAllBadges = async () => {
    try {
      const response = await axios.get('/api/reputation/badges/all');
      setAllBadges(response.data);
    } catch (err) {
      console.error('Error fetching all badges:', err);
    }
  };

  const getLevelProgress = () => {
    if (!reputation) return 0;
    const pointsIntoLevel = reputation.totalPoints % 100;
    return (pointsIntoLevel / 100) * 100;
  };

  const hasBadge = (badgeId: string) => {
    return badges.some(b => b.id === badgeId);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !reputation) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'No reputation data available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <StarIcon className="h-8 w-8 text-yellow-500" />
          Reputation & Achievements
        </h1>
        <p className="mt-2 text-gray-600">
          Track your progress and unlock badges
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Points</p>
              <p className="text-3xl font-bold text-gray-900">{reputation.totalPoints.toLocaleString()}</p>
            </div>
            <ChartBarIcon className="h-12 w-12 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Level</p>
              <p className="text-3xl font-bold text-gray-900">{reputation.level}</p>
            </div>
            <StarIcon className="h-12 w-12 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Badges Earned</p>
              <p className="text-3xl font-bold text-gray-900">{badges.length}</p>
            </div>
            <TrophyIcon className="h-12 w-12 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Level Progress */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Level {reputation.level}</h2>
            <p className="text-sm text-gray-600">
              {reputation.nextLevelPoints} points to Level {reputation.level + 1}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-purple-600">
              {getLevelProgress().toFixed(0)}%
            </div>
          </div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-4 rounded-full transition-all duration-500"
            style={{ width: `${getLevelProgress()}%` }}
          />
        </div>
      </div>

      {/* Earned Badges */}
      {badges.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrophyIcon className="h-6 w-6 text-yellow-500" />
            Your Badges
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {badges.map((badge) => (
              <div
                key={badge.id}
                className="flex items-center gap-3 p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-300 rounded-lg"
              >
                <div className="text-3xl">{badge.icon}</div>
                <div>
                  <h3 className="font-semibold text-gray-900">{badge.name}</h3>
                  <p className="text-sm text-gray-600">{badge.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Available Badges */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">All Achievements</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {allBadges.map((badge) => {
            const earned = hasBadge(badge.id);
            return (
              <div
                key={badge.id}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 ${
                  earned
                    ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300'
                    : 'bg-gray-50 border-gray-200 opacity-50'
                }`}
              >
                <div className={`text-3xl ${earned ? '' : 'grayscale'}`}>
                  {badge.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{badge.name}</h3>
                  <p className="text-sm text-gray-600">{badge.description}</p>
                  {!earned && (
                    <p className="text-xs text-gray-500 mt-1">🔒 Locked</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* How to Earn Points */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">How to Earn Points</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📝</span>
            <span><strong>+10 points</strong> for each post created</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">❤️</span>
            <span><strong>+2 points</strong> for each post like</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">💬</span>
            <span><strong>+5 points</strong> for each comment received</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">⭐</span>
            <span><strong>+50 points</strong> for each subscriber</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">💵</span>
            <span><strong>+1 point</strong> per dollar in tips</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎯</span>
            <span><strong>+100 points</strong> for reaching milestones</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReputationDashboard;
